import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="relative hidden lg:block bg-cream-paper">
        <img
          src="https://images.unsplash.com/photo-1601049541289-9b1b7bbbfe19?w=1200&q=80"
          alt="Skincare ritual"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cream-paper/30 via-transparent to-cream-paper/40" />
        <div className="relative h-full flex items-end p-10">
          <blockquote className="max-w-md text-ink">
            <p className="font-display text-3xl leading-tight">
              “Good skin is the best foundation. The rest is just play.”
            </p>
            <span className="mt-3 block text-xs uppercase tracking-[0.22em] text-rose-gold">Radiance Hub</span>
          </blockquote>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md bg-card border-border/70 rounded-2xl p-7 sm:p-8">
          <div className="flex justify-center mb-6">
            <BrandLogo size={56} variant="stacked" showTagline />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-rose-gold" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Welcome back</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            Sign in to your glow.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Continue to your beauty profile and saved favorites.
          </p>

          <form onSubmit={handle} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@radiance.com"
                className="mt-2 h-11 rounded-xl bg-card"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-2 h-11 rounded-xl bg-card"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 h-11"
              data-testid="login-submit-button"
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            New here?{' '}
            <Link to="/signup" className="text-foreground underline underline-offset-4" data-testid="link-to-signup">
              Create your beauty profile
            </Link>
          </p>
          <p className="mt-2 text-xs text-muted-foreground text-center">
            <Link to="/" className="underline underline-offset-4" data-testid="continue-as-guest-button">Continue as guest</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
